// src/pages/TrainingFollowup/TrainingFollowupPage.jsx
import { useCallback, useMemo, useState } from "react";
import { TrainingFollowupAI } from "../../lib/trainingFollowupApi";
import { buildReportHtml } from "../../lib/reportRenderer";

/* 공통 복사 버튼 */
function CopyButton({ text, className = "", label = "복사" }) {
  const onCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert("클립보드에 복사되었습니다.");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert("클립보드에 복사되었습니다.");
    }
  }, [text]);

  return (
    <button type="button" onClick={onCopy} className={className}>
      {label}
    </button>
  );
}

/* 간단 이스케이프 (오류문구 등 안전 출력용) */
function escapeHtml(s = "") {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

export default function TrainingFollowupPage() {
  // 1) 후속 메시지
  const [concernType, setConcernType] = useState("재정적 장벽");
  const [patientNotes, setPatientNotes] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");

  // 2) 아웃리치 스크립트
  const [outreachTheme, setOutreachTheme] = useState("여름 대비 다이어트");
  const [targetProfile, setTargetProfile] = useState("");
  const [scriptDraft, setScriptDraft] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState("");

  // 3) 맞춤 플랜
  const [planFocus, setPlanFocus] = useState("체중 감량 집중");
  const [planProfile, setPlanProfile] = useState("");
  const [planDraft, setPlanDraft] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState("");

  const busy = useMemo(() => msgLoading || scriptLoading || planLoading, [msgLoading, scriptLoading, planLoading]);

  // ===== 생성 핸들러들 =====
  const onGenerateMessage = useCallback(async () => {
    setMsgLoading(true);
    setMsgError("");
    setMessageDraft("");
    try {
      const res = await TrainingFollowupAI.message({
        concernType,
        patientNotes: patientNotes?.trim() || "",
      });
      setMessageDraft((res?.answer || "").trim());
    } catch (e) {
      setMsgError(e?.message || "생성 실패");
    } finally {
      setMsgLoading(false);
    }
  }, [concernType, patientNotes]);

  const onGenerateScript = useCallback(async () => {
    setScriptLoading(true);
    setScriptError("");
    setScriptDraft("");
    try {
      const res = await TrainingFollowupAI.outreach({
        theme: outreachTheme,
        profile: targetProfile?.trim() || "",
      });
      setScriptDraft((res?.answer || "").trim());
    } catch (e) {
      setScriptError(e?.message || "생성 실패");
    } finally {
      setScriptLoading(false);
    }
  }, [outreachTheme, targetProfile]);

  const onGeneratePlan = useCallback(async () => {
    if (!planProfile.trim()) {
      alert("환자 프로필 및 목표 요약을 입력해주세요.");
      return;
    }
    setPlanLoading(true);
    setPlanError("");
    setPlanDraft("");
    try {
      const res = await TrainingFollowupAI.plan({
        focus: planFocus,
        profile: planProfile.trim(),
      });
      setPlanDraft((res?.answer || "").trim());
    } catch (e) {
      setPlanError(e?.message || "생성 실패");
    } finally {
      setPlanLoading(false);
    }
  }, [planFocus, planProfile]);

  return (
    <section className="trainingFollow">
      <div id="content_title">
        <h2>AI MaC 상담(후속조치)</h2>
        <p>상담 업무 효율을 극대화하는 AI 파트너</p>
      </div>

      <div className="content">
        {/* ===== 1) AI 환자 응대 어시스턴트 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 환자 응대 어시스턴트</h3>

          <div>
            <label className="inputTitle">1. 환자의 핵심 고민 선택</label>
            <select className=" useform" value={concernType} onChange={(e) => setConcernType(e.target.value)}>
              <option value="재정적 장벽">재정적 장벽 (비용 문제)</option>
              <option value="가족 협의 필요">숙고 및 가족 협의 필요</option>
              <option value="비대면 진료 불신">비대면 진료에 대한 불신</option>
              <option value="과거 부정적 경험">과거 다이어트 실패 경험</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 환자 관련 추가 정보 (선택 사항)</label>
            <textarea rows={8} className=" useform" placeholder="예: 3개월 프로그램 비용이 예상보다 높다고 하셨음. 자녀 돌봄으로 운동 시간이 부족한 상황." value={patientNotes} onChange={(e) => setPatientNotes(e.target.value)} />
          </div>

          <div >
            <button className="submitbtn" onClick={onGenerateMessage} disabled={msgLoading || busy}>
              {msgLoading ? "생성중.." : "맞춤 후속 메시지 생성"}
            </button>
          </div>

          {(messageDraft || msgError) && (
            <div className="trf-box">
              <div className="trf-box-head">AI 생성 메시지 초안</div>
              <div className="trf-box-body">
                <textarea rows={10} readOnly  value={msgError ? `오류: ${msgError}` : messageDraft || ""} />
                <div>
                  <CopyButton text={messageDraft} className="copy" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== 2) AI 주도적 환자 관리 도우미 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 주도적 환자 관리 도우미</h3>

          <div>
            <label className="inputTitle">1. 상담 테마 선택</label>
            <select className=" useform" value={outreachTheme} onChange={(e) => setOutreachTheme(e.target.value)}>
              <option value="여름 대비 다이어트">여름 대비 다이어트</option>
              <option value="명절 후 디톡스">명절 후 디톡스 및 체중 관리</option>
              <option value="새해 건강 목표 달성">새해 건강 목표 달성</option>
              <option value="2주 체험 프로그램 소개">2주 체험 프로그램 특별 소개</option>
              <option value="건강 증진 및 활력 회복">전반적인 건강 증진 및 활력 회복</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 타겟 환자 프로필 (선택 사항)</label>
            <textarea rows={4} className=" useform" placeholder="예: 활동량이 적은 직장인, 출산 후 체중 감량을 원하는 주부, 이전 다이어트에 실패 경험이 있는 분" value={targetProfile} onChange={(e) => setTargetProfile(e.target.value)} />
          </div>

          <div >
            <button className="submitbtn" onClick={onGenerateScript} disabled={scriptLoading || busy}>
              {scriptLoading ? "생성중.." : "AI 상담 스크립트 생성"}
            </button>
          </div>

          {(scriptDraft || scriptError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 생성 스크립트</div>
              <div className="trf-box-body">
                <textarea rows={10} readOnly  value={scriptError ? `오류: ${scriptError}` : scriptDraft || ""} />
                <div>
                  <CopyButton text={scriptDraft} className="copy" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== 3) AI 맞춤 건강 플랜 제안 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 맞춤 건강 플랜 제안</h3>

          <div>
            <label className="inputTitle">1. 플랜 핵심 목표 선택</label>
            <select className=" useform" value={planFocus} onChange={(e) => setPlanFocus(e.target.value)}>
              <option value="체중 감량 집중">체중 감량 집중</option>
              <option value="활력 증진 및 에너지 회복">활력 증진 및 에너지 회복</option>
              <option value="디톡스 및 체질 개선">디톡스 및 체질 개선</option>
              <option value="스트레스 관리 및 심신 안정">스트레스 관리 및 심신 안정</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 환자 프로필 및 목표 요약</label>
            <textarea rows={8} className=" useform" placeholder="예: 40대 초반 여성, 사무직, 잦은 야근과 저녁 회식으로 스트레스가 많음. 최근 1년간 8kg 체중 증가. 3개월 내 10kg 감량 및 만성 피로 개선 희망." value={planProfile} onChange={(e) => setPlanProfile(e.target.value)} />
          </div>

          <div >
            <button className="submitbtn" onClick={onGeneratePlan} disabled={planLoading || busy}>
              {planLoading ? "생성중.." : "AI 맞춤 플랜 생성"}
            </button>
          </div>

          {(planDraft || planError) && (
            <div className="report">
              <div className="report-head">AI 생성 맞춤 건강 플랜 (초안)</div>
              <div className="report-body">
                <div className="report-body-area">
                  {planError ? (
                    <div
                      className="report-markdown"
                      dangerouslySetInnerHTML={{
                        __html: `<p class="report-paragraph">오류: ${escapeHtml(planError)}</p>`,
                      }}
                    />
                  ) : (
                    <div
                      className="report-markdown"
                      dangerouslySetInnerHTML={{
                        __html: buildReportHtml(planDraft, {
                          iconHTML: "",
                        }),
                      }}
                    />
                  )}
                </div>
                <div className="report-actions">
                  <CopyButton text={planDraft} className="copy" label="플랜 내용 복사" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
